import { useCallback, useEffect, useState } from 'react';

import API from 'app/API';
import usePersistent from 'hooks/use-persistent';
import groupBy from 'utils/group-by';

import { HistoryOfChangesItem, HistoryOfChangesRawItem, HistoryOfChangesResponse } from '../types/history-of-changes';

interface Parameters {
    id: string;
}

interface Options {
    enabled?: boolean;
    onSuccess?: (data: HistoryOfChangesItem[]) => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
}

export default function useHistoryOfChanges(parameters: Parameters, options?: Options) {
    const { id } = parameters;
    const { enabled, onSuccess, onError, onSettled } = options || {};

    const [data, setData] = useState<HistoryOfChangesItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetched, setIsFetched] = useState(false);

    const refine = useCallback((input: HistoryOfChangesRawItem[]): HistoryOfChangesItem[] => {
        const filtered = input
            .filter(({ field }) => ['description', 'title', 'epss', 'cvss2', 'cvss3'].includes(field))
            .map(({ published, ...rest }) => ({ ...rest, published: new Date(published) }));

        const grouped = groupBy(filtered, ({ published }) => published.getTime());

        const output = Object.values(grouped)
            .map((changes) => ({
                published: changes[0].published,
                changes: changes.reduce((acc, { field, value }) => ({ ...acc, [field]: value }), {})
            }))
            .sort((itemA, itemB) => itemA.published.getTime() - itemB.published.getTime())
            .map((item, index) => ({ ...item, version: index + 1 }))
            .reverse();

        return output as HistoryOfChangesItem[];
    }, []);

    const refetch = useCallback(async () => {
        try {
            setIsLoading(true);
            const url = API.SEARCH_HISTORY.url.replace('{id}', id);
            const response = await fetch(url, { method: API.SEARCH_HISTORY.method });
            const json: HistoryOfChangesResponse = await response.json();
            const data = refine(json.data.result);
            setIsFetched(true);
            setData(data);
            return data;
        } finally {
            setIsLoading(false);
        }
    }, [id, refine]);

    const persistent = usePersistent({
        isFetched,
        refetch,
        onSuccess,
        onError,
        onSettled
    });

    useEffect(() => {
        if (enabled && persistent.current.isFetched === false) {
            void persistent.current
                .refetch()
                .then((data) => persistent.current.onSuccess?.(data))
                .catch((error) => persistent.current.onError?.(error))
                .finally(() => persistent.current.onSettled?.());
        }
    }, [id, enabled, persistent]);

    return { data, refetch, isLoading, isFetched };
}
